import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import FormAccordion from "../../components/Form/FormAccordion";
import { Formgroup, ButtonGroup, Button as BigBtn } from "../Products/Forms/ProductForm.styled";
import { InputField } from "../../components/Form/FormComponents";
import { Backdrop, Box, Button, CircularProgress, Typography } from "@mui/material";
import { MdKeyboardArrowDown } from "react-icons/md";
import { SingleBank, WalletContainer, WalletHeader } from "../../auth/wallet/walletStyles";
import { fetchAllBanks, verifyBankAccount } from "../dashboard/DashboardUtils";
import { saveAvailableBanks } from "../../redux/WalletSlice";
import { FaBoxOpen } from "react-icons/fa";
import { getCurrentUserInfo } from "../../utils/UserAuth";
import Parse from "../../api/ApiConfig";

const AdminBankInfo = () => {
	const { availableBanks } = useSelector((state) => state.wallet);

	const adminData = getCurrentUserInfo();

	const [toggleBanks, setToggleBanks] = useState(false);
	const [verifyBank, setVerifyBank] = useState(null);
	const [loaders, setLoaders] = useState({
		verifyBankLoading: false,
		saveDetailsLoading: false,
	});
	const [formData, setFormData] = useState({
		bankName: adminData?.bankName || "",
		accountNumber: adminData?.accountNumber || "",
		accountName: adminData?.accountName || "",
	});

	const { bankName, accountNumber, accountName } = formData;

	const handleInputChange = (e) => {
		if (isNaN(e.target.value)) {
			return;
		}
		setFormData({ ...formData, accountNumber: e.target.value });
	};

	const saveSelectedBank = (bankName) => setFormData({ ...formData, bankName });

	const selectedBankCode = availableBanks?.find((bank) => bank?.name === bankName)?.uuid;

	// FUNCTION TO VERIFY ACCOUNT DETAILS
	const VerifyBankDetails = async (info) => {
		setLoaders((prev) => ({ ...prev, verifyBankLoading: true }));
		try {
			const data = await verifyBankAccount(info);
			setVerifyBank(data);
		} catch (error) {
			toast.error(error);
		} finally {
			setLoaders((prev) => ({ ...prev, verifyBankLoading: false }));
		}
	};

	// SUBMIT BANK INFORMATION TO PARSE
	const handleSaveBankDetails = async (e) => {
		e.preventDefault();

		if (bankName === "" || accountName === "" || accountNumber === "") {
			toast.error("Feilds cannot be empty");
			return;
		}
		if (accountNumber.length < 10) {
			toast.error("Account number is invalid");
			return;
		}

		try {
			setLoaders((prev) => ({ ...prev, saveDetailsLoading: true }));
			const query = new Parse.Query("User");
			const object = await query.get(Parse.User.current()?.id);

			await object.save(formData);

			toast.success("Your bank information updated successfully.");
		} catch (error) {
			console.log(error);
			if (error?.message === "Network Error") {
				toast.error("Oops, Service provider has been disconnected!");
			} else {
				toast.error("Failed to update bank details.");
			}
		} finally {
			setLoaders((prev) => ({ ...prev, saveDetailsLoading: false }));
		}
	};

	// AUTO VERIFY BANK DETAILS
	useEffect(() => {
		if (verifyBank) {
			return;
		}
		if (bankName === "" && accountNumber === "") {
			return;
		} else if (accountNumber?.length < 10) {
			return;
		}
		if (accountNumber === getCurrentUserInfo()?.accountNumber) {
			return;
		} else if (selectedBankCode) {
			setFormData((prev) => ({ ...prev, accountName: "" }));
			VerifyBankDetails({
				accountNumber,
				bankUUID: selectedBankCode,
			});
		}
	}, [bankName, accountNumber]);

	// SET GENERATED ACCOUNT NAME TO STATE
	useEffect(() => {
		if (loaders.verifyBankLoading) {
			return;
		} else if (verifyBank) {
			setFormData((prev) => ({
				...prev,
				accountName: verifyBank?.message?.includes("Invalid Account for account number")
					? "No account matches these details"
					: verifyBank?.destinationAccountHolderNameAtBank && verifyBank?.destinationAccountHolderNameAtBank,
			}));
		}
	}, [verifyBank]);

	useEffect(() => {
		if (toggleBanks) {
			document.body.style.overflow = "hidden";
		}
		return () => {
			// eslint-disable-next-line no-undef
			document.body.style.overflow = "auto";
		};
	}, [toggleBanks]);

	return (
		<>
			<Backdrop open={toggleBanks} sx={{ zIndex: 13 }}>
				<AllBanksModal
					onClose={() => setToggleBanks(false)}
					onSelect={saveSelectedBank}
					bankName={bankName}
				/>
			</Backdrop>
			{loaders.saveDetailsLoading && (
				<Backdrop
					sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
					open={loaders.saveDetailsLoading}>
					<CircularProgress color='inherit' />
				</Backdrop>
			)}
			<FormAccordion className='mt-4' title='Bank Information' open={true}>
				<form onSubmit={handleSaveBankDetails}>
					<div className='grid grid-cols-3 gap-5 mt-5'>
						<Formgroup type='users'>
							<label htmlFor='bankName'>Bank Name</label>
							<Box
								display='flex'
								justifyContent='space-between'
								alignItems='center'
								border='1px solid #b8c8cf'
								paddingY={1}
								paddingX={1.5}
								borderRadius='6px'
								fontFamily='inherit'
								sx={{ cursor: "pointer" }}
								onClick={() => setToggleBanks(true)}>
								<Typography variant='body2' component='p'>
									{bankName === "" ? "-- select bank -- " : bankName}
								</Typography>
								<MdKeyboardArrowDown size={20} />
							</Box>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='accountNumber'>Account Number</label>
							<InputField
								name='accountNumber'
								handleChange={handleInputChange}
								isEditable={true}
								value={accountNumber}
								maxLength={10}
								type='tel'
							/>
						</Formgroup>
						<Formgroup type='users'>
							<label htmlFor='accountName'>Account Name</label>
							<InputField
								placeholder='Account Name'
								isEditable={false}
								value={loaders.verifyBankLoading ? "searching..." : accountName}
							/>
						</Formgroup>
					</div>
					<ButtonGroup>
						<BigBtn type='submit' color='#ffffff' backgroundcolor='#003A50'>
							<p>Save Details</p>
						</BigBtn>
					</ButtonGroup>
				</form>
			</FormAccordion>
		</>
	);
};

AdminBankInfo.propTypes = {};

export default AdminBankInfo;

const AllBanksModal = ({ bankName, onClose, onSelect }) => {
	const [searchText, setSearchText] = useState("");
	const dispatch = useDispatch();
	const { availableBanks } = useSelector((state) => state.wallet);
	const [loading, setLoading] = useState(false);

	const onSaveSubmit = () => {
		onClose();
		setSearchText("");
	};

	const getAllBanks = async () => {
		setLoading(true);
		try {
			const data = await fetchAllBanks();
			dispatch(saveAvailableBanks(data));
		} catch (error) {
			toast.error(error);
		} finally {
			setLoading(false);
		}
	};

	const filteredBanks = availableBanks
		?.filter((bank) => bank?.name.toLowerCase().includes(searchText.toLowerCase()))
		?.sort((a, b) => (b.name > a.name ? -1 : b.name < a.name ? 1 : 0));

	useEffect(() => {
		if (availableBanks) {
			return;
		} else {
			getAllBanks();
		}
	}, []);

	return (
		<WalletContainer>
			<>
				<WalletHeader headerText='Select Your Bank' onCloseModal={onClose} />
				<Box marginY={1}>
					<Formgroup type='users'>
						<InputField
							name='accountNumber'
							isEditable={true}
							placeholder='find your bank faster...'
							value={searchText}
							handleChange={(e) => setSearchText(e.target.value)}
						/>
						<div
							style={{ overflowY: "auto", paddingRight: "4px", minHeight: "100px", maxHeight: "200px" }}
							className='scroll-wrapper'>
							{loading ? (
								<Box height='100px' display='flex' alignItems='center' justifyContent='center'>
									<CircularProgress color='primary' />
								</Box>
							) : filteredBanks?.length > 0 ? (
								<>
									{filteredBanks.map((bank, i) => (
										<SingleBank
											key={i}
											onClick={() => onSelect(bank?.name)}
											className={bankName === bank?.name ? "dark-bg" : "light-bg"}>
											{bank?.name}
										</SingleBank>
									))}
								</>
							) : (
								<div className='flex-col justify-center' style={{ height: "100px" }}>
									<FaBoxOpen size={35} color='#b5b5b5' />
									<p style={{ fontWeight: 500, color: "#B2B2B2", fontSize: "13px" }}>
										No bank matches the search text
									</p>
								</div>
							)}
						</div>
						{filteredBanks?.length > 0 && (
							<Button
								variant='contained'
								color='primary'
								sx={{ marginTop: "5px" }}
								disabled={bankName === ""}
								onClick={onSaveSubmit}>
								save
							</Button>
						)}
					</Formgroup>
				</Box>
			</>
		</WalletContainer>
	);
};

AllBanksModal.propTypes = {
	onClose: PropTypes.func,
	onSelect: PropTypes.func,
	bankName: PropTypes.string,
};
